import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { 
  Plus, Trash2, Edit2, Save, X, GripVertical, 
  Bold, Italic, Underline, List, ListOrdered, 
  AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Printer
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { noteAPI } from '../../services/api'
import { useModalClose } from '../../hooks/useModalClose'
import toast from 'react-hot-toast'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface NotesWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Note {
  id: number
  title: string
  content: string
}

const GOOGLE_FONTS = [
  { name: 'Default', value: 'inherit' },
  { name: 'Inter', value: 'Inter, sans-serif' },
  { name: 'Roboto', value: 'Roboto, sans-serif' },
  { name: 'Open Sans', value: 'Open Sans, sans-serif' },
  { name: 'Lato', value: 'Lato, sans-serif' },
  { name: 'Montserrat', value: 'Montserrat, sans-serif' },
  { name: 'Poppins', value: 'Poppins, sans-serif' },
  { name: 'Playfair Display', value: 'Playfair Display, serif' },
  { name: 'Merriweather', value: 'Merriweather, serif' },
  { name: 'Roboto Mono', value: 'Roboto Mono, monospace' },
  { name: 'Source Code Pro', value: 'Source Code Pro, monospace' },
  { name: 'Crimson Text', value: 'Crimson Text, serif' },
  { name: 'Raleway', value: 'Raleway, sans-serif' },
  { name: 'Nunito', value: 'Nunito, sans-serif' },
  { name: 'Ubuntu', value: 'Ubuntu, sans-serif' },
]

export default function NotesWidget({ widget, isExpanded }: NotesWidgetProps) {
  const queryClient = useQueryClient()
  const [editingNote, setEditingNote] = useState<Note | null>(null)
  const [editingTitle, setEditingTitle] = useState(false)
  const [tempTitle, setTempTitle] = useState('')
  const [tempContent, setTempContent] = useState('')
  const [localNotes, setLocalNotes] = useState<Note[]>([])
  const [selectedFont, setSelectedFont] = useState('inherit')
  const contentEditableRef = useRef<HTMLDivElement>(null)
  const { handleBackdropClick } = useModalClose(() => setEditingNote(null))

  const { data: notes = [] } = useQuery({
    queryKey: ['notes'],
    queryFn: async () => {
      const { data } = await noteAPI.getAll()
      setLocalNotes(data)
      return data
    },
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = localNotes.findIndex((note) => note.id === active.id)
      const newIndex = localNotes.findIndex((note) => note.id === over.id)

      const newNotes = arrayMove(localNotes, oldIndex, newIndex)
      setLocalNotes(newNotes)

      // Update positions in backend
      try {
        await Promise.all(
          newNotes.map((note, index) =>
            noteAPI.update(note.id, { position: index })
          )
        )
      } catch (error) {
        toast.error('Failed to save order')
        queryClient.invalidateQueries({ queryKey: ['notes'] })
      }
    }
  }

  const handleAddNote = async () => {
    try {
      const { data } = await noteAPI.create({
        title: 'New Note',
        content: '',
      })
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      
      // Open the newly created note in modal
      setEditingNote(data)
      setTempTitle(data.title)
      setTempContent(data.content)
      setEditingTitle(false)
      
      toast.success('Note created!')
    } catch (error) {
      toast.error('Failed to create note')
    }
  }

  const handleOpenNote = (note: Note) => {
    setEditingNote(note)
    setTempTitle(note.title)
    setTempContent(note.content)
    setEditingTitle(false)
    setSelectedFont('inherit')
    
    // Set content to contentEditable div
    setTimeout(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = note.content || ''
      }
    }, 0)
  }

  const handleSaveNote = async () => {
    if (!editingNote) return
    
    // Get content from contentEditable div
    const content = contentEditableRef.current?.innerHTML || ''
    
    try {
      await noteAPI.update(editingNote.id, { 
        title: tempTitle.trim() || 'Untitled Note',
        content: content 
      })
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      toast.success('Note saved!')
      setEditingNote(null)
    } catch (error) {
      toast.error('Failed to save note')
    }
  }

  const applyFormat = (command: string, value: string | undefined = undefined) => {
    // Handle list commands specially since document.execCommand is unreliable
    if (command === 'insertUnorderedList') {
      insertList('ul')
      return
    }
    if (command === 'insertOrderedList') {
      insertList('ol')
      return
    }

    // For other commands, try document.execCommand
    try {
      document.execCommand(command, false, value)
    } catch (error) {
      console.warn(`execCommand '${command}' failed:`, error)
    }
    contentEditableRef.current?.focus()
  }

  const insertList = (listType: 'ul' | 'ol') => {
    const selection = window.getSelection()
    if (!selection || !contentEditableRef.current) return

    const range = selection.getRangeAt(0)
    const listElement = document.createElement(listType)
    const listItem = document.createElement('li')

    // Add some initial content to the list item
    listItem.innerHTML = '<br>'
    listElement.appendChild(listItem)

    // Insert the list at the current cursor position
    range.deleteContents()
    range.insertNode(listElement)

    // Move cursor to the list item
    const newRange = document.createRange()
    newRange.setStart(listItem, 0)
    newRange.setEnd(listItem, 0)
    selection.removeAllRanges()
    selection.addRange(newRange)

    contentEditableRef.current.focus()
  }

  const handleFontChange = (font: string) => {
    setSelectedFont(font)
    if (contentEditableRef.current) {
      contentEditableRef.current.style.fontFamily = font
    }
  }

  const handlePrint = () => {
    if (!editingNote) return
    
    // Create a temporary print window
    const printWindow = window.open('', '', 'width=800,height=600')
    if (!printWindow) {
      toast.error('Please allow popups to print')
      return
    }
    
    // Get the note content
    const content = contentEditableRef.current?.innerHTML || tempContent
    
    // Create the print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${tempTitle}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Roboto:wght@400;700&family=Open+Sans:wght@400;600;700&family=Lato:wght@400;700&family=Montserrat:wght@400;600;700&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&family=Raleway:wght@400;600;700&family=Nunito:wght@400;700&family=Ubuntu:wght@400;700&display=swap');
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: ${selectedFont};
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
              line-height: 1.6;
              color: #333;
            }
            
            h1 {
              font-size: 28px;
              font-weight: 700;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e5e7eb;
            }
            
            .content {
              font-size: 14px;
            }
            
            .content p {
              margin-bottom: 12px;
            }
            
            .content ul, .content ol {
              margin: 12px 0;
              padding-left: 24px;
            }
            
            .content li {
              margin-bottom: 6px;
            }
            
            .content a {
              color: #0183c6;
              text-decoration: underline;
            }
            
            @media print {
              body {
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <h1>${tempTitle}</h1>
          <div class="content">${content}</div>
        </body>
      </html>
    `)
    
    printWindow.document.close()
    
    // Wait for fonts to load, then print
    setTimeout(() => {
      printWindow.focus()
      printWindow.print()
      printWindow.close()
    }, 500)
  }

  const handleDeleteNote = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!confirm('Are you sure you want to delete this note?')) return
    
    try {
      await noteAPI.delete(id)
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      
      // Close modal if deleting the currently open note
      if (editingNote?.id === id) {
        setEditingNote(null)
      }
      
      toast.success('Note deleted!')
    } catch (error) {
      toast.error('Failed to delete note')
    }
  }

  if (notes.length === 0) {
    return (
      <>
        <div className="h-full flex flex-col items-center justify-center p-4">
          <p className="text-gray-500 mb-4">No notes yet</p>
          <button
            onClick={handleAddNote}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            <Plus size={16} />
            Create Your First Note
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="h-full flex flex-col p-3">
        {/* Add Note Button */}
        <button
          onClick={handleAddNote}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition mb-3"
        >
          <Plus size={16} />
          <span className="text-sm">New Note</span>
        </button>

        {/* Notes Grid */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className={isExpanded ? "grid grid-cols-3 gap-2 overflow-y-auto" : "space-y-2 overflow-y-auto flex-1"}>
            <SortableContext
              items={localNotes.map((note) => note.id)}
              strategy={verticalListSortingStrategy}
            >
              {localNotes.map((note: Note) => (
                <SortableNoteCard
                  key={note.id}
                  note={note}
                  onOpen={() => handleOpenNote(note)}
                  onDelete={(e) => handleDeleteNote(note.id, e)}
                />
              ))}
            </SortableContext>
          </div>
        </DndContext>
      </div>

      {/* Note Edit Modal */}
      {editingNote && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={handleBackdropClick}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-5xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 flex-1">
                {editingTitle ? (
                  <>
                    <input
                      type="text"
                      value={tempTitle}
                      onChange={(e) => setTempTitle(e.target.value)}
                      className="flex-1 px-3 py-2 border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setEditingTitle(false)
                        if (e.key === 'Escape') {
                          setTempTitle(editingNote.title)
                          setEditingTitle(false)
                        }
                      }}
                    />
                    <button
                      onClick={() => setEditingTitle(false)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                    >
                      <Save size={20} />
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 flex-1">{tempTitle}</h2>
                    <button
                      onClick={() => setEditingTitle(true)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      title="Edit title"
                    >
                      <Edit2 size={20} />
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePrint}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="Print note"
                >
                  <Printer size={20} />
                </button>
                <button
                  onClick={async (e) => {
                    if (editingNote) {
                      await handleDeleteNote(editingNote.id, e)
                      setEditingNote(null)
                    }
                  }}
                  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                  title="Delete note"
                  style={{ opacity: 1 }}
                >
                  <Trash2 size={20} style={{ strokeWidth: 2.5 }} />
                </button>
                <button
                  onClick={() => setEditingNote(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Close"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Formatting Toolbar */}
            <div className="flex items-center gap-1 p-2 bg-gray-100 rounded-lg mb-3 flex-wrap">
              {/* Font Selector */}
              <select
                value={selectedFont}
                onChange={(e) => handleFontChange(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ fontFamily: selectedFont }}
              >
                {GOOGLE_FONTS.map((font) => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                    {font.name}
                  </option>
                ))}
              </select>

              <div className="w-px h-6 bg-gray-300 mx-1"></div>

              {/* Text Formatting */}
              <button
                onClick={() => applyFormat('bold')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Bold (Ctrl+B)"
              >
                <Bold size={18} />
              </button>
              <button
                onClick={() => applyFormat('italic')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Italic (Ctrl+I)"
              >
                <Italic size={18} />
              </button>
              <button
                onClick={() => applyFormat('underline')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Underline (Ctrl+U)"
              >
                <Underline size={18} />
              </button>

              <div className="w-px h-6 bg-gray-300 mx-1"></div>

              {/* Lists */}
              <button
                onClick={() => applyFormat('insertUnorderedList')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Bullet List"
              >
                <List size={18} />
              </button>
              <button
                onClick={() => applyFormat('insertOrderedList')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Numbered List"
              >
                <ListOrdered size={18} />
              </button>

              <div className="w-px h-6 bg-gray-300 mx-1"></div>

              {/* Alignment */}
              <button
                onClick={() => applyFormat('justifyLeft')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Align Left"
              >
                <AlignLeft size={18} />
              </button>
              <button
                onClick={() => applyFormat('justifyCenter')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Align Center"
              >
                <AlignCenter size={18} />
              </button>
              <button
                onClick={() => applyFormat('justifyRight')}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Align Right"
              >
                <AlignRight size={18} />
              </button>

              <div className="w-px h-6 bg-gray-300 mx-1"></div>

              {/* Link */}
              <button
                onClick={() => {
                  const url = prompt('Enter URL:')
                  if (url) applyFormat('createLink', url)
                }}
                className="p-2 hover:bg-gray-200 rounded transition"
                title="Insert Link"
              >
                <LinkIcon size={18} />
              </button>
            </div>

            {/* Content Editor */}
            <div
              ref={contentEditableRef}
              contentEditable
              className="flex-1 overflow-y-auto border border-gray-300 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 mb-4 min-h-[400px]"
              style={{
                fontFamily: selectedFont,
                lineHeight: '1.6',
              }}
              suppressContentEditableWarning
              onInput={() => {
                // Keep content synced for potential future use
                setTempContent(contentEditableRef.current?.innerHTML || '')
              }}
            />

            {/* Modal Footer */}
            <div className="flex gap-3">
              <button
                onClick={handleSaveNote}
                className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold"
              >
                Save Note
              </button>
              <button
                onClick={() => setEditingNote(null)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

// Sortable Note Card Component
function SortableNoteCard({ note, onOpen, onDelete }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: note.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div className="group p-3 border border-gray-200 rounded-lg cursor-pointer transition hover:border-primary-300 hover:shadow-sm bg-white relative">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={14} className="text-gray-400" />
        </div>

        {/* Content */}
        <div onClick={onOpen}>
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-semibold text-gray-900 text-sm truncate flex-1 pl-6">
              {note.title}
            </h3>
            <button
              onClick={onDelete}
              className="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-all hover:scale-110"
              title="Delete note"
              style={{ opacity: 1 }}
            >
              <Trash2 size={14} style={{ strokeWidth: 2.5 }} />
            </button>
          </div>
          <p className="text-xs text-gray-600 line-clamp-2 pl-6">
            {note.content || 'No content yet...'}
          </p>
        </div>
      </div>
    </div>
  )
}

